/**
 * "← Back" returns to the exact view the user left — one contract for the whole app.
 *
 * A page that opens a detail page passes, in router state, where it was (`returnTo`: path +
 * query + hash; the query is where every page keeps its filters) and the router state it had itself
 * (`returnState`). The detail page's Back navigates to `returnTo` WITH `returnState`, so a
 * chain unwinds step by step: list → OKR → child OKR → Back → OKR → Back → the filtered list.
 *
 * Before this existed, each detail page hard-coded its Back target (`/okrs`,
 * `/admin?tab=users`), so every filter, tab and period the user had set was gone. It was
 * fixed page by page as customers reported it; `returnTo.guard.test.ts` now fails the build
 * if a page opens a detail route without going through `useOpenWithReturn`.
 *
 * Pure functions only; the hooks live in `hooks/useReturnNavigation.ts`.
 */

/** Deeper chains keep working; only the oldest steps are forgotten, never the newest. */
export const MAX_RETURN_DEPTH = 10;

export interface ReturnNavState {
  returnTo: string;
  returnState?: unknown;
}

const isSafePath = (raw: unknown): raw is string =>
  typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/\\');

const depthOf = (state: unknown): number => {
  let depth = 0;
  let cursor = state as { returnState?: unknown } | null | undefined;
  while (cursor && typeof cursor === 'object' && 'returnTo' in cursor) {
    depth += 1;
    cursor = cursor.returnState as { returnState?: unknown } | null | undefined;
  }
  return depth;
};

/** Copy a chain, dropping everything past `keep` steps. */
const truncate = (state: unknown, keep: number): unknown => {
  if (keep <= 0 || !state || typeof state !== 'object' || !('returnTo' in state)) return undefined;
  const { returnTo, returnState } = state as ReturnNavState;
  const rest = truncate(returnState, keep - 1);
  return rest === undefined ? { returnTo } : { returnTo, returnState: rest };
};

/**
 * Router state to pass when leaving `current` for a detail page.
 */
export function buildReturnState(current: {
  pathname: string;
  search: string;
  hash?: string;
  state?: unknown;
}): ReturnNavState {
  const returnTo = `${current.pathname}${current.search}${current.hash ?? ''}`;
  const prior = current.state;
  if (!prior || typeof prior !== 'object' || !('returnTo' in prior)) return { returnTo };
  const kept = depthOf(prior) >= MAX_RETURN_DEPTH ? truncate(prior, MAX_RETURN_DEPTH - 1) : prior;
  return { returnTo, returnState: kept };
}

/**
 * Where Back goes, and with which router state.
 *
 * Only a same-app absolute path is accepted. `//host/...` is protocol-relative and would
 * leave the app; anything that is not a string is ignored. Everything rejected falls back.
 */
export function resolveReturn(state: unknown, fallback: string): { to: string; state?: unknown } {
  const nav = state as Partial<ReturnNavState> | null | undefined;
  if (!nav || !isSafePath(nav.returnTo)) return { to: fallback };
  return nav.returnState === undefined ? { to: nav.returnTo } : { to: nav.returnTo, state: nav.returnState };
}

/** The Back target alone. Kept for callers that only need the path. */
export function resolveReturnTo(state: unknown, fallback: string): string {
  return resolveReturn(state, fallback).to;
}
